import { useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { House, ChartBar, Storefront, MagnifyingGlass, Users, SignOut, Gear, List, X, ClipboardText } from "@phosphor-icons/react";

const items = [
  { to: "/", label: "Visão Geral", icon: House, end: true, testid: "nav-overview" },
  { to: "/portfolio", label: "Portfólio Base", icon: Storefront, testid: "nav-portfolio" },
  { to: "/rastrear", label: "Rastrear Produto", icon: MagnifyingGlass, testid: "nav-track" },
  { to: "/comparativo", label: "Comparativo", icon: ChartBar, testid: "nav-compare" },
  { to: "/players", label: "Players de Mercado", icon: Users, testid: "nav-players" },
  { to: "/diagnostico", label: "Diagnóstico", icon: ClipboardText, testid: "nav-diagnostico" },
  { to: "/configuracoes", label: "Configurações", icon: Gear, testid: "nav-settings" },
];

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const location = useLocation();

  // Close drawer when route changes
  const handleNav = () => setDrawerOpen(false);

  const NavItem = ({ it, onClick, idPrefix = "" }) => (
    <NavLink
      to={it.to}
      end={it.end}
      onClick={onClick}
      data-testid={idPrefix ? `${idPrefix}-${it.testid}` : it.testid}
      className={({ isActive }) =>
        "group flex items-center gap-3 px-3 py-3 text-sm transition-all border-l-2 " +
        (isActive
          ? "border-l-[var(--gold)] bg-[var(--bg-surface)] text-[var(--text-primary)]"
          : "border-l-transparent text-[var(--text-secondary)] hover:bg-[var(--bg-surface)] hover:text-[var(--text-primary)]")
      }
    >
      <it.icon size={18} weight="duotone" />
      <span className="tracking-wide">{it.label}</span>
    </NavLink>
  );

  return (
    <div className="min-h-screen flex" style={{ background: "var(--bg-base)" }}>
      {/* Desktop Sidebar */}
      <aside
        className="w-64 shrink-0 hidden md:flex flex-col border-r"
        style={{ borderColor: "var(--border-default)", background: "var(--bg-sidebar)" }}
      >
        <div className="px-6 py-6 border-b flex items-center gap-3" style={{ borderColor: "var(--border-default)" }}>
          <img src={LOGO_URL} alt="" className="w-11 h-11 object-contain" data-testid="sidebar-logo" />
          <div>
            <div className="label-eyebrow">Inteligência</div>
            <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>
              Pesquisa Customizável
            </div>
          </div>
        </div>

        <nav className="flex-1 px-3 py-6 space-y-1">
          {items.map((it) => <NavItem key={it.to} it={it} />)}
        </nav>

        <div className="px-6 py-5 border-t" style={{ borderColor: "var(--border-default)" }}>
          <div className="label-eyebrow mb-2">Sessão</div>
          <div className="flex items-center justify-between">
            <div className="text-sm truncate" style={{ color: "var(--text-primary)" }}>{user?.username}</div>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="btn-ghost px-2 py-1 text-xs rounded-sm flex items-center gap-1"
              title="Sair"
            >
              <SignOut size={14} weight="bold" /> Sair
            </button>
          </div>
        </div>
      </aside>

      {/* Mobile Top Bar */}
      <div
        className="md:hidden fixed top-0 left-0 right-0 z-40 flex items-center justify-between px-4 py-3 border-b"
        style={{ borderColor: "var(--border-default)", background: "rgba(250,250,246,0.95)", backdropFilter: "blur(12px)" }}
      >
        <button
          onClick={() => setDrawerOpen(true)}
          data-testid="mobile-menu-open"
          className="btn-ghost p-2 rounded-sm"
          aria-label="Abrir menu"
        >
          <List size={20} weight="bold" />
        </button>
        <div className="flex items-center gap-2">
          <img src={LOGO_URL} alt="" className="w-8 h-8 object-contain" />
          <span className="text-xs tracking-wider uppercase font-medium" style={{ color: "var(--text-primary)" }}>Pesquisa Customizável</span>
        </div>
        <button
          onClick={logout}
          className="btn-ghost p-2 rounded-sm"
          data-testid="logout-btn-mobile"
          aria-label="Sair"
        >
          <SignOut size={18} weight="bold" />
        </button>
      </div>

      {/* Mobile Drawer */}
      {drawerOpen && (
        <div
          className="md:hidden fixed inset-0 z-50"
          data-testid="mobile-drawer"
          onClick={() => setDrawerOpen(false)}
        >
          <div className="absolute inset-0" style={{ background: "rgba(13,36,25,0.55)" }} />
          <aside
            onClick={(e) => e.stopPropagation()}
            className="absolute top-0 left-0 bottom-0 w-72 max-w-[85vw] flex flex-col border-r fade-up"
            style={{ borderColor: "var(--border-default)", background: "var(--bg-sidebar)" }}
          >
            <div className="px-5 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--border-default)" }}>
              <div className="flex items-center gap-3">
                <img src={LOGO_URL} alt="" className="w-10 h-10 object-contain" />
                <div>
                  <div className="label-eyebrow">Inteligência</div>
                  <div className="text-sm font-medium" style={{ color: "var(--text-primary)" }}>Pesquisa Customizável</div>
                </div>
              </div>
              <button
                onClick={() => setDrawerOpen(false)}
                className="btn-ghost p-1.5 rounded-sm"
                aria-label="Fechar"
                data-testid="mobile-menu-close"
              >
                <X size={16} weight="bold" />
              </button>
            </div>

            <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
              {items.map((it) => <NavItem key={it.to} it={it} onClick={handleNav} idPrefix="mobile" />)}
            </nav>

            <div className="px-5 py-4 border-t" style={{ borderColor: "var(--border-default)" }}>
              <div className="label-eyebrow mb-2">Sessão</div>
              <div className="flex items-center justify-between">
                <div className="text-sm truncate" style={{ color: "var(--text-primary)" }}>{user?.username}</div>
                <button
                  onClick={logout}
                  className="btn-ghost px-2 py-1 text-xs rounded-sm flex items-center gap-1"
                >
                  <SignOut size={14} weight="bold" /> Sair
                </button>
              </div>
            </div>
          </aside>
        </div>
      )}

      <main className="flex-1 min-h-screen relative w-full">
        <div className="px-4 sm:px-6 md:px-10 pt-16 md:pt-10 pb-16 md:pb-12 max-w-[1400px] mx-auto md:mx-0">
          {children}
        </div>
      </main>
    </div>
  );
}
